import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Search,
  Users,
  Building2,
  Mail,
  Phone,
  MapPin,
  FolderKanban,
  ChevronRight,
  Loader2,
  ArrowLeft,
  Plus,
  UserPlus
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function CustomersPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", address: "", source: "walk_in", source_details: "" });

  useEffect(() => {
    fetchCustomers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.toLowerCase();
    const email = (customer.email || "").toLowerCase();
    const phone = (customer.phone || "").toLowerCase();
    const company = (customer.company || "").toLowerCase();
    return fullName.includes(query) || email.includes(query) || phone.includes(query) || company.includes(query);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "qualified": return "bg-blue-100 text-blue-700";
      case "converted": return "bg-green-100 text-green-700";
      case "won": return "bg-emerald-100 text-emerald-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName?.[0] || "").toUpperCase()}${(lastName?.[0] || "").toUpperCase()}`;
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.first_name || !newCustomer.last_name) {
      toast.error("First and last name required");
      return;
    }
    if (["referral", "other"].includes(newCustomer.source) && !newCustomer.source_details.trim()) {
      toast.error(newCustomer.source === "referral" ? "Please note who referred them" : "Please describe how this customer found us");
      return;
    }
    try {
      const res = await axios.post(`${API}/leads`, { ...newCustomer, source_details: newCustomer.source_details.trim() || null, status: "qualified" });
      toast.success("Customer added!");
      setShowAddModal(false);
      setNewCustomer({ first_name: "", last_name: "", email: "", phone: "", company: "", address: "", source: "walk_in", source_details: "" });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add customer");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Customers
          </h1>
          <p className="text-slate-600 mt-1">
            {customers.length} active customer{customers.length !== 1 ? "s" : ""} • Qualified, Converted & Won leads
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} data-testid="add-customer-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Add Customer
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search customers by name, email, phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="customer-search"
        />
      </div>

      {/* Customers Grid */}
      {filteredCustomers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.map(customer => (
            <Card 
              key={customer.lead_id} 
              className="hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => navigate(`/customers/${customer.lead_id}`)}
              data-testid={`customer-card-${customer.lead_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <Avatar className="h-12 w-12 flex-shrink-0">
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 font-medium">
                      {getInitials(customer.first_name, customer.last_name)}
                    </AvatarFallback>
                  </Avatar>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {customer.first_name} {customer.last_name}
                      </h3>
                      <ChevronRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>

                    {customer.company && (
                      <div className="flex items-center gap-1 text-sm text-slate-600 mt-0.5">
                        <Building2 className="w-3 h-3" />
                        <span className="truncate">{customer.company}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={`${getStatusColor(customer.status)} text-xs`}>
                        {customer.status}
                      </Badge>
                      {customer.project_count > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <FolderKanban className="w-3 h-3 mr-1" />
                          {customer.project_count} project{customer.project_count !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="mt-3 space-y-1 text-xs text-slate-500">
                      {customer.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{customer.email}</span>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          <span>{customer.phone}</span>
                        </div>
                      )}
                      {customer.address && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{customer.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">
              {searchQuery ? "No customers match your search" : "No customers yet"}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {searchQuery 
                ? "Try a different search term" 
                : "Leads become customers when marked as Qualified, Converted, or Won"
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add Customer Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-indigo-600" /> Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>First Name *</Label><Input value={newCustomer.first_name} onChange={(e) => setNewCustomer({ ...newCustomer, first_name: e.target.value })} placeholder="John" /></div>
              <div><Label>Last Name *</Label><Input value={newCustomer.last_name} onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })} placeholder="Smith" /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} placeholder="john@example.com" /></div>
            <div><Label>Phone</Label><Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} placeholder="905-555-1234" /></div>
            <div><Label>Company</Label><Input value={newCustomer.company} onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })} placeholder="Company name" /></div>
            <div><Label>Address</Label><Input value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })} placeholder="123 Main St" /></div>
            <div>
              <Label>Source *</Label>
              <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-white" value={newCustomer.source}
                onChange={(e) => setNewCustomer({ ...newCustomer, source: e.target.value, source_details: "" })} data-testid="customer-source-select">
                <option value="walk_in">Walk in</option>
                <option value="call_in">Call in</option>
                <option value="referral">Referral</option>
                <option value="other">Other</option>
              </select>
            </div>
            {["referral", "other"].includes(newCustomer.source) && (
              <div>
                <Label>{newCustomer.source === "referral" ? "Referred by *" : "How did they find us? *"}</Label>
                <Textarea rows={2} className="mt-1" value={newCustomer.source_details}
                  onChange={(e) => setNewCustomer({ ...newCustomer, source_details: e.target.value })}
                  placeholder={newCustomer.source === "referral" ? "e.g. Past client Jane Smith, contractor Mike at ABC Builders…" : "e.g. Saw our truck, home show booth, neighbour's kitchen…"}
                  data-testid="customer-source-details" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddCustomer}>Add Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
